// src/api/fakeApi.js
// 修改后的版本 - 数据持久化保存

// 模拟延迟
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// 从localStorage加载数据，如果没有则使用默认数据
const loadFromStorage = (key, defaultValue) => {
  try {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : defaultValue;
  } catch (error) {
    console.error(`加载 ${key} 失败:`, error);
    return defaultValue;
  }
};

// 保存数据到localStorage
const saveToStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error(`保存 ${key} 失败:`, error);
  }
};

// 初始化数据库
const initializeDatabase = () => {
  return {
    users: loadFromStorage('fakeApi_users', [
      { id: 1, username: 'admin', password: '123456', name: '管理员', email: 'admin@example.com', avatar: '管' },
      { id: 2, username: 'user1', password: '123456', name: '张三', email: 'user1@example.com', avatar: '张' },
    ]),
    posts: loadFromStorage('fakeApi_posts', [
      { 
        id: 1, 
        title: 'React Hooks完全指南', 
        author: '张三', 
        authorAvatar: '张',
        content: '<h2>什么是 React Hooks？</h2><p>React Hooks 是 React 16.8 的新增特性...</p>',
        date: '2024-01-15',
        views: 1200,
        likes: 56,
        comments: 23,
        tags: ['React', 'JavaScript', '前端']
      },
      { 
        id: 2, 
        title: 'TypeScript类型系统深入解析', 
        author: '李四', 
        authorAvatar: '李',
        content: '<p>TypeScript的类型系统是其核心特性之一...</p>',
        date: '2024-01-14',
        views: 890,
        likes: 45,
        comments: 12,
        tags: ['TypeScript', 'JavaScript']
      },
    ]),
    comments: loadFromStorage('fakeApi_comments', [
      { id: 1, postId: 1, author: '小明', content: '好文章！', date: '2024-01-15 10:30' },
      { id: 2, postId: 1, author: '小红', content: '学到了很多', date: '2024-01-15 14:20' },
    ])
  };
};

let fakeDatabase = initializeDatabase();

// API响应格式包装
const createResponse = (success, data, message = '') => ({
  success,
  data,
  message,
  timestamp: new Date().toISOString()
});

// 保存数据库到localStorage
const saveDatabase = () => {
  saveToStorage('fakeApi_users', fakeDatabase.users);
  saveToStorage('fakeApi_posts', fakeDatabase.posts);
  saveToStorage('fakeApi_comments', fakeDatabase.comments);
};

// ============ 用户相关API ============
export const authApi = {
  // 登录
  login: async (username, password) => {
    await delay(500);
    
    const user = fakeDatabase.users.find(
      u => u.username === username && u.password === password
    );
    
    if (user) {
      // 移除密码再返回
      const { password: _, ...userWithoutPassword } = user;
      return createResponse(true, {
        user: userWithoutPassword,
        token: 'fake-jwt-token-' + Date.now()
      });
    }
    
    return createResponse(false, null, '用户名或密码错误');
  },
  
  // 注册
  register: async (userData) => {
    await delay(500);
    
    // 检查用户名是否存在
    const exists = fakeDatabase.users.some(u => u.username === userData.username);
    if (exists) {
      return createResponse(false, null, '用户名已存在');
    }
    
    const newUser = {
      id: fakeDatabase.users.length + 1,
      ...userData,
      avatar: userData.name?.charAt(0) || '用'
    };
    
    fakeDatabase.users.push(newUser);
    saveDatabase(); // 保存到localStorage
    
    const { password: _, ...userWithoutPassword } = newUser;
    return createResponse(true, {
      user: userWithoutPassword,
      token: 'fake-jwt-token-' + Date.now()
    });
  }
};

// ============ 文章相关API ============
export const postsApi = {
  // 获取文章列表
  getPosts: async (page = 1, pageSize = 10, search = '') => {
    await delay(300);
    
    let filteredPosts = [...fakeDatabase.posts];
    
    // 搜索过滤
    if (search) {
      const searchLower = search.toLowerCase();
      filteredPosts = filteredPosts.filter(post => 
        post.title.toLowerCase().includes(searchLower) ||
        post.content.toLowerCase().includes(searchLower) ||
        (post.tags && post.tags.some(tag => tag.toLowerCase().includes(searchLower)))
      );
    }
    
    // 分页
    const total = filteredPosts.length;
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    const data = filteredPosts.slice(start, end);
    
    return createResponse(true, {
      posts: data,
      pagination: {
        page,
        pageSize,
        total,
        totalPages: Math.ceil(total / pageSize)
      }
    });
  },
  
  // 获取单篇文章
  getPost: async (id) => {
    await delay(300);
    
    const post = fakeDatabase.posts.find(p => p.id === id);
    if (!post) {
      return createResponse(false, null, '文章不存在');
    }
    
    // 增加阅读量
    post.views = (post.views || 0) + 1;
    saveDatabase(); // 保存到localStorage
    
    // 获取该文章的评论
    const comments = fakeDatabase.comments.filter(c => c.postId === id);
    
    return createResponse(true, {
      ...post,
      comments
    });
  },
  
  // 创建文章
  createPost: async (postData) => {
    await delay(500);
    
    const newPost = {
      id: Date.now(), // 使用时间戳作为ID
      ...postData,
      date: new Date().toISOString().split('T')[0],
      views: 0,
      likes: 0,
      comments: 0,
      tags: postData.tags || []
    };
    
    fakeDatabase.posts.unshift(newPost); // 新文章放在前面
    saveDatabase(); // 保存到localStorage
    
    return createResponse(true, newPost, '文章发布成功');
  },
  
  // 点赞文章
  likePost: async (id) => {
    await delay(300);
    
    const post = fakeDatabase.posts.find(p => p.id === id);
    if (!post) {
      return createResponse(false, null, '文章不存在');
    }
    
    post.likes = (post.likes || 0) + 1;
    saveDatabase(); // 保存到localStorage
    
    return createResponse(true, { likes: post.likes }, '点赞成功');
  }
};

// ============ 评论相关API ============
export const commentsApi = {
  // 添加评论
  addComment: async (postId, commentData) => {
    await delay(500);
    
    const post = fakeDatabase.posts.find(p => p.id === postId);
    if (!post) {
      return createResponse(false, null, '文章不存在');
    }
    
    const newComment = {
      id: fakeDatabase.comments.length + 1,
      postId,
      ...commentData,
      date: new Date().toISOString()
    };
    
    fakeDatabase.comments.push(newComment);
    post.comments = (post.comments || 0) + 1;
    saveDatabase(); // 保存到localStorage
    
    return createResponse(true, newComment, '评论发布成功');
  },
  
  // 获取文章评论
  getPostComments: async (postId) => {
    await delay(300);
    
    const comments = fakeDatabase.comments.filter(c => c.postId === postId);
    return createResponse(true, comments);
  }
};

// ============ 导出所有API ============
export const fakeApi = {
  auth: authApi,
  posts: postsApi,
  comments: commentsApi,
  
  // 重置数据库（测试用）
  resetDatabase: () => {
    localStorage.removeItem('fakeApi_users');
    localStorage.removeItem('fakeApi_posts');
    localStorage.removeItem('fakeApi_comments');
    fakeDatabase = initializeDatabase();
    console.log('数据库已重置');
  }
};

export default fakeApi;